
import { useState } from "react";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

const DataRequest = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [requestType, setRequestType] = useState("access");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Error",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate API call
    try {
      // In a real implementation, this would be an actual API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setIsSuccess(true);
      toast({
        title: "Request Submitted",
        description: "We've received your data request and will process it shortly.",
      });
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: "There was a problem submitting your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-fraudeye-primary">
      <div className="container max-w-2xl px-4 py-12">
        <Button variant="outline" size="sm" className="mb-8" asChild>
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Link>
        </Button>
        
        <Card>
          <CardHeader>
            <CardTitle>Data Request Form</CardTitle>
            <CardDescription>
              Submit a request to exercise your data privacy rights under GDPR, CCPA, or other applicable privacy regulations.
            </CardDescription>
          </CardHeader>
          
          {isSuccess ? (
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="mb-4 rounded-full bg-green-100 p-3">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="mb-2 text-2xl font-semibold">Request Submitted</h3>
                <p className="mb-6 text-muted-foreground">
                  We've received your data request and will process it within 30 days.
                  A confirmation has been sent to {email}.
                </p>
                <Button asChild>
                  <Link to="/">Return to Home</Link>
                </Button>
              </div>
            </CardContent>
          ) : (
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    We'll use this email to verify your identity and communicate about your request.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <Label>Request Type</Label>
                  <RadioGroup value={requestType} onValueChange={setRequestType}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="access" id="access" />
                      <Label htmlFor="access" className="cursor-pointer">
                        Data Access (Get a copy of your data)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="deletion" id="deletion" />
                      <Label htmlFor="deletion" className="cursor-pointer">
                        Data Deletion (Request to erase your data)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="correction" id="correction" />
                      <Label htmlFor="correction" className="cursor-pointer">
                        Data Correction (Fix inaccurate data)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="portability" id="portability" />
                      <Label htmlFor="portability" className="cursor-pointer">
                        Data Portability (Transfer your data)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="restriction" id="restriction" />
                      <Label htmlFor="restriction" className="cursor-pointer">
                        Processing Restriction (Limit how we use your data)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="other" id="other" />
                      <Label htmlFor="other" className="cursor-pointer">
                        Other Request
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="additionalInfo">Additional Information</Label>
                  <Textarea
                    id="additionalInfo"
                    placeholder="Please provide any additional details about your request"
                    rows={4}
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                  />
                </div>
              </CardContent>
              
              <CardFooter className="flex justify-between">
                <Button variant="outline" type="button" asChild>
                  <Link to="/privacy-policy">Cancel</Link>
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Submit Request
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
        
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>
            We take your privacy rights seriously and will process your request within 30 days.
            <br />
            For urgent matters, please contact our Data Protection Officer at dpo@fraudeye.com.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DataRequest;
